import { type FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AdminGroupView,
  AdminUserView,
  AllocationOverview,
  MemberQuotaDefault,
  QuotaCycle
} from '../../api.js';
import { CREDITS_PATTERN, toCredits, toMillis } from '../../components/AllocationPanel.js';
import { describeError } from '../../errors.js';
import type { AdminHttpClient } from '../../http.js';
import type { ToastMessage } from '../../types.js';

export type MemberNodeKind = 'tenant' | 'group';
export type MemberCapMode = 'ceiling' | 'reservation';

const CYCLES: QuotaCycle[] = ['weekly', 'daily', 'monthly', 'lifetime'];

/** Moved here from the Allocations screen in PR-4: a per-member default is a
 *  usage LIMIT, not a budget, so it belongs with the other limits. */
export function MemberDefaultPanel({
  http,
  groups,
  users,
  tenantId,
  onToast
}: {
  http: AdminHttpClient;
  groups: AdminGroupView[];
  users: AdminUserView[];
  tenantId: string;
  onToast: (toast: ToastMessage) => void;
}) {
  const { t } = useTranslation();
  const [nodeKind, setNodeKind] = useState<MemberNodeKind>('tenant');
  const [nodeId, setNodeId] = useState('');
  const [current, setCurrent] = useState<MemberQuotaDefault | null>(null);
  const [pool, setPool] = useState<AllocationOverview | undefined>();
  const [credit, setCredit] = useState('');
  const [cycle, setCycle] = useState<QuotaCycle>('lifetime');
  const [mode, setMode] = useState<MemberCapMode>('ceiling');
  const [busy, setBusy] = useState(false);

  const nodes = useMemo(
    () =>
      nodeKind === 'tenant'
        ? [{ id: tenantId, label: tenantId }]
        : groups.map((group) => ({ id: group.id, label: group.name })),
    [groups, nodeKind, tenantId]
  );
  const activeUserIds = useMemo(
    () => new Set(users.filter((user) => user.status === 'active').map((user) => user.id)),
    [users]
  );
  const memberCount = useMemo(() => {
    if (nodeKind === 'tenant') return activeUserIds.size;
    const group = groups.find((candidate) => candidate.id === nodeId);
    return group ? group.memberIds.filter((id) => activeUserIds.has(id)).length : 0;
  }, [activeUserIds, groups, nodeId, nodeKind]);

  const load = useCallback(
    async (kind: MemberNodeKind, id: string) => {
      if (!id) {
        setCurrent(null);
        setPool(undefined);
        setCredit('');
        setCycle('lifetime');
        setMode('ceiling');
        return;
      }
      try {
        const [row, overview] = await Promise.all([
          http.getMemberQuotaDefault(kind, id),
          kind === 'tenant' ? http.tenantGetOwnAllocation() : http.getSubjectAllocation('group', id)
        ]);
        setCurrent(row);
        setPool(overview);
        setCredit(
          row?.perMemberCreditMillis === undefined ? '' : String(toCredits(row.perMemberCreditMillis))
        );
        setCycle(row?.cycle ?? 'lifetime');
        setMode(row?.mode ?? 'ceiling');
      } catch (error) {
        onToast({ tone: 'error', message: describeError(error, t) });
      }
    },
    [http, onToast, t]
  );

  useEffect(() => {
    if (nodes.length === 0) {
      setNodeId('');
      return;
    }
    if (!nodes.some((node) => node.id === nodeId)) setNodeId(nodes[0]?.id ?? '');
  }, [nodeId, nodes]);

  useEffect(() => {
    void load(nodeKind, nodeId);
  }, [load, nodeId, nodeKind]);

  const creditMillis =
    credit.trim() !== '' && CREDITS_PATTERN.test(credit.trim())
      ? toMillis(credit.trim())
      : undefined;
  const exposureMillis = creditMillis === undefined ? undefined : creditMillis * memberCount;
  const poolMillis = pool?.unlimited ? undefined : (pool?.credit.granted ?? 0);
  const reservationExceeds =
    mode === 'reservation' &&
    exposureMillis !== undefined &&
    !pool?.unlimited &&
    exposureMillis > (poolMillis ?? 0);

  function save(event: FormEvent) {
    event.preventDefault();
    if (!nodeId) return;
    const raw = credit.trim();
    if (raw !== '' && !CREDITS_PATTERN.test(raw)) {
      onToast({ tone: 'error', message: t('memberCap.capInvalid') });
      return;
    }
    // One default per node: switching the cycle REPLACES the existing row, so a
    // lifetime default silently disappears when someone picks weekly.
    if (current && current.cycle !== cycle) {
      const confirmed = window.confirm(
        t('memberCap.confirmCycleChange', {
          node: nodeId,
          from: t(`creditLimits.cycle_${current.cycle}`),
          to: t(`creditLimits.cycle_${cycle}`)
        })
      );
      if (!confirmed) return;
    }
    setBusy(true);
    void http
      .setMemberQuotaDefault({
        nodeKind,
        nodeId,
        perMemberCreditMillis: raw === '' ? undefined : toMillis(raw),
        cycle,
        mode
      })
      .then(async () => {
        await load(nodeKind, nodeId);
        onToast({ tone: 'success', message: t('memberCap.saved') });
      })
      .catch((error) => onToast({ tone: 'error', message: describeError(error, t) }))
      .finally(() => setBusy(false));
  }

  return (
    <form className="panel member-cap-form" onSubmit={save}>
      <div className="member-cap-heading">
        <h2>{t('memberCap.title')}</h2>
        <span className="muted">
          {current ? t('memberCap.currentlySet') : t('memberCap.notSet')}
        </span>
      </div>
      <label>
        <span>{t('memberCap.nodeKind')}</span>
        <select
          value={nodeKind}
          onChange={(event) => setNodeKind(event.target.value as MemberNodeKind)}
          disabled={busy}
        >
          <option value="tenant">{t('memberCap.tenant')}</option>
          <option value="group">{t('memberCap.group')}</option>
        </select>
      </label>
      <label>
        <span>{t('memberCap.node')}</span>
        <select
          value={nodeId}
          onChange={(event) => setNodeId(event.target.value)}
          disabled={busy || nodes.length === 0}
        >
          {nodes.map((node) => (
            <option key={node.id} value={node.id}>
              {node.label}
            </option>
          ))}
        </select>
      </label>
      <label>
        <span>{t('memberCap.perMemberCredits')}</span>
        <input
          value={credit}
          inputMode="decimal"
          placeholder={t('memberCap.perMemberPlaceholder')}
          onChange={(event) => setCredit(event.target.value)}
          disabled={busy}
          autoComplete="off"
        />
      </label>

      <div className="member-cap-cycle">
        <span>{t('memberCap.cycle')}</span>
        <div className="segmented-control">
          {CYCLES.map((option) => (
            <button
              key={option}
              type="button"
              className={cycle === option ? 'active' : ''}
              aria-pressed={cycle === option}
              onClick={() => {
                setCycle(option);
                // A periodic default can only be a ceiling; keep the form in a
                // state the server would accept rather than letting it 400.
                if (option !== 'lifetime') setMode('ceiling');
              }}
              disabled={busy}
            >
              {t(`creditLimits.cycle_${option}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="member-cap-mode">
        <span>{t('memberCap.mode')}</span>
        <div className="segmented-control">
          {(['ceiling', 'reservation'] as const).map((option) => (
            <button
              key={option}
              type="button"
              className={mode === option ? 'active' : ''}
              aria-pressed={mode === option}
              onClick={() => setMode(option)}
              disabled={busy || (option === 'reservation' && cycle !== 'lifetime')}
              title={
                option === 'reservation' && cycle !== 'lifetime'
                  ? t('memberCap.reservationLifetimeOnly')
                  : undefined
              }
            >
              {t(`memberCap.${option}`)}
            </button>
          ))}
        </div>
      </div>

      <p className="muted">{t('memberCap.replacesExisting')}</p>
      <p className={reservationExceeds ? 'warning member-cap-preview' : 'muted member-cap-preview'}>
        {exposureMillis === undefined
          ? t('memberCap.fallbackPreview')
          : t('memberCap.exposurePreview', {
              members: memberCount,
              perMember: toCredits(creditMillis ?? 0),
              exposure: toCredits(exposureMillis)
            })}
      </p>
      <p className="muted">{t('creditLimits.newMemberFullCycle')}</p>
      <p className="muted">{t('memberCap.perUserOverrideHint')}</p>
      <button disabled={busy || !nodeId}>{t('memberCap.save')}</button>
    </form>
  );
}
